/**
 * src/config/generatorExperienceGroups.js
 *
 * Presentation helpers for the three generator experiences defined by
 * New Products Designs/VISINVOKE_DND_NEW_GENERATOR_EXPANSION_AUTHORITY_2026-09-28.md:
 * STANDARD D&D GENERATORS, QUIRKY, CHAOTIC & FUNNY and TABLEPLAY, CHAOS & CONSEQUENCES.
 *
 * MEMBERSHIP IS NEVER DECIDED HERE. Which generator belongs to which group, the group names, the
 * approved positioning lines and the counts all come from the server catalogue
 * (standalone_generators.experience_groups and each item's primary_group / secondary_groups), which
 * reads the one shared classification file backend/generator_experience_groups.json. This module only
 * gives each group its visual identity and answers "is this item in that group" from the item's own
 * server-supplied fields.
 */

export const GROUP_QUERY_PARAM = 'group';
export const ALL_GROUPS = 'all';

// Visual identity only (colours, art treatment, badge). Keyed by the server's group key; an unknown
// key falls back to a neutral treatment rather than being hidden.
const IDENTITY = {
  standard: {
    accent: '#E3B341', accent2: '#8A5A12', bright: '#F6CD68', glow: 'rgba(227,179,65,0.24)',
    tint: 'linear-gradient(135deg, rgba(24,17,6,0.72) 0%, rgba(138,90,18,0.42) 55%, rgba(24,17,6,0.78) 100%)',
    base: 'linear-gradient(135deg, #171006 0%, #8A5A12 55%, #2a1a07 100%)',
    badge: '\u{1F4DC}', mood: 'Classic',
  },
  quirky: {
    accent: '#E14BD6', accent2: '#7A1C87', bright: '#F47BEA', glow: 'rgba(225,75,214,0.24)',
    tint: 'linear-gradient(135deg, rgba(28,6,30,0.70) 0%, rgba(122,28,135,0.44) 55%, rgba(28,6,30,0.78) 100%)',
    base: 'linear-gradient(135deg, #1c061e 0%, #7A1C87 55%, #2b0d33 100%)',
    badge: '\u{1F410}', mood: 'Unexpected',
  },
  tableplay: {
    accent: '#F26A2E', accent2: '#8C2A0C', bright: '#FF915C', glow: 'rgba(242,106,46,0.24)',
    tint: 'linear-gradient(135deg, rgba(30,10,4,0.70) 0%, rgba(140,42,12,0.44) 55%, rgba(30,10,4,0.78) 100%)',
    base: 'linear-gradient(135deg, #1e0a04 0%, #8C2A0C 55%, #2d1006 100%)',
    badge: '\u{1F3B2}', mood: 'Live at the table',
  },
};
const NEUTRAL = {
  accent: '#22C58B', accent2: '#0E7A50', bright: '#4BE0A8', glow: 'rgba(34,197,139,0.22)',
  tint: 'linear-gradient(135deg, rgba(5,20,14,0.72) 0%, rgba(14,122,80,0.40) 55%, rgba(5,20,14,0.78) 100%)',
  base: 'linear-gradient(135deg, #0B2A20 0%, #0E7A50 58%, #12351f 100%)',
  badge: '\u{1F9ED}', mood: 'Generators',
};

export function groupIdentity(key) {
  return IDENTITY[key] || NEUTRAL;
}

/** The server's group list, or [] when the catalogue predates the three-group authority. */
export function serverGroups(payload) {
  const groups = payload && Array.isArray(payload.experience_groups) ? payload.experience_groups : [];
  return groups.filter((group) => group && typeof group.key === 'string' && group.name);
}

/** The `?group=` value, validated against the server's own group keys. Anything else means all. */
export function groupFromSearch(search, groups) {
  let value = '';
  try { value = new URLSearchParams(search || '').get(GROUP_QUERY_PARAM) || ''; } catch (err) { value = ''; }
  const keys = new Set((groups || []).map((group) => group.key));
  return keys.has(value) ? value : ALL_GROUPS;
}

export function groupRoute(baseRoute, key) {
  return key && key !== ALL_GROUPS ? `${baseRoute}?${GROUP_QUERY_PARAM}=${encodeURIComponent(key)}` : baseRoute;
}

/** Primary membership: the only membership that is ever counted. */
export function isPrimaryMember(item, key) {
  return !!item && item.primary_group === key;
}

/** Discovery membership: primary, or surfaced through a secondary tag (never counted twice). */
export function itemInGroup(item, key) {
  if (!key || key === ALL_GROUPS) return true;
  if (!item) return false;
  return item.primary_group === key
    || (Array.isArray(item.secondary_groups) && item.secondary_groups.includes(key));
}

/** Primary members first, then related generators, each in the catalogue's own order. */
export function orderForGroup(items, key) {
  const list = Array.isArray(items) ? items : [];
  if (!key || key === ALL_GROUPS) return list;
  return [...list.filter((item) => isPrimaryMember(item, key)),
    ...list.filter((item) => !isPrimaryMember(item, key) && itemInGroup(item, key))];
}
